/**
 * LEFT-HAND comping voicings — the pianist's left hand alone under a right
 * hand that is busy with the melody (the trio texture: piano, bass and drums,
 * the head in the right hand, the changes in the left). Where the two-hand
 * families in pianoVoicings.ts model a comping pianist with both hands free,
 * these are the one-hand vocabulary every method teaches for the left hand:
 *
 * - `lh-shell`        — the three-note shell, R-3-7 and its compact inversions
 *                       (Bud Powell's texture; the 6th stands in for the 7th on
 *                       sixth chords).
 * - `lh-root-seventh` — the open two-note shells: root and 7th, root and 3rd,
 *                       and the stretched root-and-10th where the hand allows.
 * - `lh-rootless`     — the Bill Evans / Wynton Kelly rootless forms, 3-5-7-9
 *                       (A) and 7-9-3-5 (B), the dominant taking its 13 for the
 *                       5 (see `rootlessOffsets`); the bassist has the root.
 * - `lh-close`        — four-way close, every chord tone under the one hand.
 *
 * Constructions are the shared enumerators, so a shell here IS the shell the
 * Lab draws — only the REGISTER and the hand are the left hand's. The register
 * is the classic left-hand band: nothing below E2 (the low interval limits in
 * `isMuddy` do the fine work above that) and nothing above G4, where a voicing
 * stops sounding like an accompaniment. Under a melody the hand also stays
 * BELOW the line: a candidate whose top reaches the lowest melody note sounding
 * in the chord is dropped (`ceiling`), unless that would leave nothing — a
 * melody dipping to G3 must not silence the comp.
 *
 * Candidates carry NO hand split: the whole voicing is the one hand, and the
 * phrase search then pins the voicing's TOP like any one-hand family, which
 * is the voice a listener follows in a left-hand comp.
 */
import { CHORD_TYPES, type ChordTypeId } from './chords';
import {
  enumeratePiano,
  intervalOf,
  placeStack,
  type PianoEnumerateOptions,
  type PianoVoicing,
} from './pianoVoicings';
import { pitchClassOf } from './notes';
import type { Inversion } from './voicing';

export type PianoLeftHandFamily = 'lh-rootless' | 'lh-shell' | 'lh-root-seventh' | 'lh-close';

export const PIANO_LEFT_HAND_FAMILIES: { id: PianoLeftHandFamily; label: string; hint: string }[] =
  [
    {
      id: 'lh-rootless',
      label: 'Rootless',
      hint: 'A and B forms, 3-5-7-9 and 7-9-3-5, the bass has the root',
    },
    {
      id: 'lh-shell',
      label: 'Shells',
      hint: 'Root, 3rd and 7th in one hand, compact inversions',
    },
    {
      id: 'lh-root-seventh',
      label: 'Root and 7th',
      hint: 'Open two-note shells: root under the 7th, the 3rd or the 10th',
    },
    {
      id: 'lh-close',
      label: 'Close position',
      hint: 'Four-way close, every chord tone under the left hand',
    },
  ];

export function isLeftHandFamily(family: string | undefined): family is PianoLeftHandFamily {
  return PIANO_LEFT_HAND_FAMILIES.some((f) => f.id === family);
}

/** The left hand's band: E2 up to G4. */
export const LH_COMP_REGISTER = { low: 40, high: 67 };
/** One left hand's reach for a chord (a 9th); the stretched 10th is admitted for dyads only. */
export const LH_COMP_SPAN = 14;
export const LH_TENTH_SPAN = 16;
/** Where the left hand's bottom prefers to open (D3). */
export const LH_ANCHOR = 50;

const ROTATIONS: Inversion[] = [0, 1, 2, 3];
const mod12 = (n: number) => ((n % 12) + 12) % 12;

export interface LeftHandOptions {
  avoidB9?: boolean;
  displayRoot?: string;
  /** The highest pitch the hand may reach — one below the lowest melody note
   * sounding in the chord. Undefined = no melody, the register alone. */
  ceiling?: number;
}

/** Root under a guide tone: R-7 (or R-6), R-3 and the stretched R-10; a
 * chord with no seventh (a triad) offers R-3 and R-5. */
function rootAndGuide(
  rootName: string,
  chordType: ChordTypeId,
  base: PianoEnumerateOptions,
): PianoVoicing[] {
  const intervals = new Set(CHORD_TYPES[chordType].intervals.map(mod12));
  const root = pitchClassOf(rootName);
  const third = intervalOf(chordType, [4, 3, 5, 2]);
  const seventh = intervalOf(chordType, [10, 11, 9]);
  const pairs: { above: number; span: number }[] = [];
  if (seventh != null) pairs.push({ above: seventh, span: LH_COMP_SPAN });
  if (third != null) {
    pairs.push({ above: third, span: LH_COMP_SPAN });
    pairs.push({ above: third + 12, span: LH_TENTH_SPAN });
  }
  if (seventh == null) {
    const fifth = [7, 6, 8].find((iv) => intervals.has(iv));
    if (fifth != null) pairs.push({ above: fifth, span: LH_COMP_SPAN });
  }
  return pairs.flatMap(({ above, span }, index) =>
    placeStack(
      [root, mod12(root + above)],
      rootName,
      chordType,
      Math.min(3, index) as Inversion,
      'shell',
      { ...base, maxHandSpan: span },
      [0, above],
    ),
  );
}

export function enumerateLeftHand(
  rootName: string,
  chordType: ChordTypeId,
  family: PianoLeftHandFamily,
  options: LeftHandOptions = {},
): PianoVoicing[] {
  const base: PianoEnumerateOptions = {
    register: LH_COMP_REGISTER,
    maxHandSpan: LH_COMP_SPAN,
    avoidB9: options.avoidB9,
    displayRoot: options.displayRoot,
  };
  let out: PianoVoicing[];
  switch (family) {
    case 'lh-rootless':
      out = ROTATIONS.flatMap((r) => enumeratePiano(rootName, chordType, r, 'rootless', base));
      break;
    case 'lh-shell':
      out = ROTATIONS.flatMap((r) => enumeratePiano(rootName, chordType, r, 'shell', base));
      break;
    case 'lh-close':
      out = ROTATIONS.flatMap((r) => enumeratePiano(rootName, chordType, r, 'close', base));
      break;
    case 'lh-root-seventh':
      out = rootAndGuide(rootName, chordType, base);
      break;
  }
  // One hand, no split: the search pins the voicing's top like any one-hand family.
  out = out.map(({ hands: _hands, ...rest }) => rest);
  if (options.ceiling != null) {
    const under = out.filter((v) => v.midis[v.midis.length - 1] <= options.ceiling!);
    if (under.length) out = under;
  }
  return [...new Map(out.map((v) => [v.midis.join(','), v])).values()];
}

/**
 * Which piano family the comp actually plays for an instrument choice: the
 * left-hand instrument takes a left-hand family (its default rootless), the
 * two-hand piano a two-hand one — so switching instruments never leaves the
 * band on a family the other hand cannot play.
 */
export function effectivePianoFamily<F extends string>(
  instrument: 'guitar' | 'piano' | 'piano-left',
  family: F,
): F | PianoLeftHandFamily | 'two-hand' {
  if (instrument === 'piano-left') return isLeftHandFamily(family) ? family : 'lh-rootless';
  return isLeftHandFamily(family) ? 'two-hand' : family;
}
